/**
 * ⚖ Rebuilding an ask-user card from a PARKED MISSION, after a reload.
 *
 * ## The defect this exists for
 *
 * `ui-tester` run `ui-20260906-0033`: an interview card was on screen at
 * 00:48:50Z, gone after a reload, while the mission stayed parked with all four
 * questions intact server-side and the assistant kept telling the lawyer *"as
 * perguntas já estão na tela"*. The card mounts from the `lx_ask_user` tool
 * broadcast and lives in component state; an EventStream frame is not replayed,
 * so a reload had nothing to rebuild it from. One unanswerable question holds
 * the case's only admission slot for its TTL.
 *
 * ## Why the source is the MISSION and not the browser
 *
 * The first design here was client-side persistence, and it was worse. A
 * restored-from-localStorage card is a claim that the agent is still waiting
 * which the SPA cannot verify — it would cheerfully resurrect a dead ask. The
 * mission's own `GET /missions/:id/awaiting` answers the question it is
 * actually asking: it 409s `not_awaiting` the moment the park is gone. So the
 * card is rebuilt only while the thing it represents is provably still true.
 *
 * ## 🚨 The rule that shapes this file: ANSWERABILITY GATES THE SURFACE
 *
 * The wizard's takeover is TOTAL — while it is up the composer is UNMOUNTED,
 * not disabled (`lexide-ask-user-guide.md` §2, operator-ratified). That makes
 * an over-eager rehydration actively dangerous:
 *
 * > Restoring a NON-answerable ask as a full takeover would unmount the
 * > composer and take away the lawyer's only working recovery — asking the
 * > assistant to show the questions again, which is measured to work in 14 s.
 * > We would have replaced a recoverable dead end with an unrecoverable one.
 *
 * The two park kinds share the `awaiting_user` STATE but not their doors
 * (`mission-service-guide.md` §7-bis): a `user` park has the v1 chat-mediated
 * answer path, a `handback` park has **no REST door at all** — only the
 * in-process bridge. So:
 *
 * | park kind                | surface                | why |
 * |---|---|---|
 * | `user`, within the cap   | the full wizard        | the lawyer is who it waits for, and answering works |
 * | `user`, OVER the cap     | questions, NO send     | ⚖ R1 — the wizard's per-ask cap is a ratified C-1 rule and its takeover is total, so an over-cap wizard would unmount the composer for a wall of text. The questions are still shown; only the WIZARD is withheld, and the copy tells him he can answer in the conversation. |
 * | `handback`, HUMAN-owed   | questions, NO send     | ⚖ R6 — no REST door, so no send control; but a CONCIERGE-addressed park is owed by the LAWYER and his chat reply IS the path, so the copy says so |
 * | `handback`, AGENT-owed   | questions, NO send     | no door exists AND nobody is waiting on him; a send control would be a lie |
 * | anything else            | questions, NO send     | OPEN vocabulary ⇒ degrade to the safe side |
 *
 * ⚠ **FOUR rows, THREE different sentences, and the surface is identical for the
 * last three.** That is the trap this table exists to stop: the shape of the
 * surface is decided by the MECHANISM (`answerable` — can an answer typed here
 * reach the mission?) and the WORDS by ATTRIBUTION (`humanOwed` — is the lawyer
 * the one being waited on?). They are different questions and they disagree on
 * row 3.
 *
 * ⇒ Until ⚖ R6 one predicate decided both, so row 3 inherited row 4's sentence
 * and told the lawyer he could not answer a park that was waiting on HIM — the
 * reassuring-direction lie, aimed at the one person who could have cleared it.
 * Merging any two of these sentences re-creates it.
 *
 * Pure and IO-free so the rules above are unit-testable; the caller owns the
 * fetching.
 */

import type { MissionAwaitingView } from "@/api/lx-mission";
import {
  ASK_USER_LIMITS,
  parseAskUserEvent,
  type AskUserCardData,
  type AskUserQuestion,
} from "@/lib/ask-user";
import { isHumanOwedCounterparty } from "@/lib/agents/human-owed";

/** What the mount path should render for a parked mission. */
export interface RehydratedAsk {
  card: AskUserCardData;
  /**
   * TRUE only for a park whose answers have somewhere to go. Gates the
   * takeover: false ⇒ show the questions, never a send affordance.
   */
  answerable: boolean;
  /** The server's own `kind`, carried verbatim for display/logging. */
  parkKind: string;
  /**
   * ⚖ R6 — does a PERSON owe this reply? SEPARATE FROM `answerable` ON PURPOSE,
   * and the split is the whole point of R6.
   *
   * `answerable` is about the MECHANISM: can an answer typed into THIS surface
   * reach the mission? For a `handback` park the answer is no — there is no REST
   * door (§7-bis), only the in-process bus bridge — so the send control stays
   * withheld and that has not changed.
   *
   * `humanOwed` is about ATTRIBUTION: is the lawyer the one being waited on? A
   * CONCIERGE-addressed handback is owed BY HIM even though this surface cannot
   * submit it, because his ordinary chat reply IS the path — the concierge holds
   * the answering tool (`extensions/lexiun-core/src/bus/answer-interview-tool.ts`)
   * and `tester-galvao` measured that round trip end to end on a live 5-point
   * park: plain prose in, `answeredAt` written, all five ids parsed.
   *
   * ⇒ Merging the two is what made the notice tell such a lawyer he could not
   * answer. `answerable` decides the CONTROL; `humanOwed` decides the COPY.
   */
  humanOwed: boolean;
  /**
   * ⚖ R1 — the ask carries MORE questions than the wizard's per-ask cap.
   *
   * 🚨 This exists so removing the count refusal cannot smuggle an over-cap ask
   * into the WIZARD. The cap is a ratified product rule on that surface
   * (`lx-ask-user.ts`: *"A cap, not a target — the anti-wall-of-text rule
   * (miniplan C-1) is the whole point of this surface"*), and the wizard's
   * takeover is TOTAL, so an over-cap wizard would unmount the composer to show
   * a wall of text. The read-only notice has no such problem: no takeover, no
   * send affordance, a compact `<ol>`.
   *
   * ⇒ `answerable && overAskCap` ⇒ show the NOTICE, never the wizard. The
   * questions are still the lawyer's to see; only the answering surface is
   * withheld, and the copy says so rather than implying he has no way to reply.
   */
  overAskCap: boolean;
}

/**
 * The one park kind a human can actually answer in v1.
 *
 * ⚠ A closed allow-list read from the SERVER's vocabulary, deliberately — the
 * same shape as `case-unreachable`'s gone-codes. Widening it is how a send
 * button appears over a door that does not exist.
 */
const ANSWERABLE_PARK_KINDS: ReadonlySet<string> = new Set(["user"]);

/**
 * Dig the questions out of the unflattened briefing parts.
 *
 * The shape is `briefing.parts[i].data.questions` (the path `ui-tester`
 * measured on the live park). Written to survive a part list that is ordered
 * differently or carries other parts, because "parts[0]" is an observation of
 * one specimen, not a contract — and a wrong index here would render an EMPTY
 * card, which is the false-claim direction.
 */
export function questionsFromBriefing(view: MissionAwaitingView): unknown[] | null {
  const parts = view.briefing?.parts;
  if (!Array.isArray(parts)) return null;
  for (const part of parts) {
    const data = (part as { data?: { questions?: unknown } } | null)?.data;
    if (data && Array.isArray(data.questions) && data.questions.length > 0) {
      return data.questions;
    }
  }
  return null;
}

/**
 * ⚖ The mission's interview questions are a DIFFERENT DIALECT from
 * `lx_ask_user`'s, and this is the translation.
 *
 * Measured from the live park `ui-tester` captured
 * (`probes/step5-mission-awaiting.json`,
 * `record.awaitingHandback.briefing.parts[0].data.questions`):
 *
 *     { id, topic, question, why, recommended }      ← the mission leaf
 *     { id, topic, prompt,   explanation, choices }  ← what the wizard parses
 *
 * `id` and `topic` happen to agree; `question`/`why` do not, and `recommended`
 * has no counterpart at all. Feeding the raw mission shape to
 * `parseAskUserEvent` therefore returns null on the missing `prompt` — it fails
 * CLOSED, correctly, which is why this translation has to be explicit rather
 * than hopeful.
 *
 * ⚠ Two things this does NOT do. It does not invent `choices`: these questions
 * are free-text by construction, and fabricating options would put words in the
 * lawyer's mouth. And it does not drop `recommended` — that is the leaf's
 * advice to the lawyer, so it is folded into the explanation where the wizard
 * already renders prose, rather than silently discarded.
 *
 * ⚠ This mapping is a CROSS-LANE COUPLING with no schema binding the two sides.
 * If the mission's leaf renames a field, this returns questions with empty
 * prompts and the card vanishes — a silent regression. Flagged in the handback;
 * the tests below pin the real field names against the captured specimen so the
 * break is loud here instead of quiet in the UI.
 */
/**
 * Choose the widest COMPLETE explanation that fits the wizard's cap.
 *
 * Exported for the tests, and because the ladder is a product decision rather
 * than a formatting detail: what gets dropped when the mission is more verbose
 * than the wizard allows. Never returns a truncation — see the caller.
 */
export function pickExplanation(why: string, recommended: string): string {
  const cap = ASK_USER_LIMITS.maxExplanationChars;
  const both = [why, recommended].filter(Boolean).join(" ");
  if (both.length <= cap) return both;
  // `why` first: it states the REASON the question is being asked, which is
  // what makes the question answerable. `recommended` is advice about the
  // answer and is the safer half to lose.
  if (why && why.length <= cap) return why;
  if (recommended && recommended.length <= cap) return recommended;
  return "";
}

export function missionQuestionToAskShape(raw: unknown): Record<string, unknown> | null {
  if (typeof raw !== "object" || raw === null) return null;
  const q = raw as Record<string, unknown>;
  const prompt = typeof q.question === "string" ? q.question : "";
  if (!prompt) return null;
  const why = typeof q.why === "string" ? q.why.trim() : "";
  const recommended = typeof q.recommended === "string" ? q.recommended.trim() : "";
  // Both are prose the lawyer should see; joined rather than one overwriting
  // the other, and neither fabricated when absent.
  //
  // 🚨 THE JOIN MUST FIT THE TOOL'S CAP, because `parseAskUserEvent` FAILS
  // CLOSED on an over-cap `explanation` and one bad question discards the WHOLE
  // card. Found live by `ui-tester` 2026-09-07 on the preserved park: joined
  // lengths 141 / 186 / 152 / 161 against a cap of 160 — q4 over by ONE
  // character — so a mission with four good questions rendered NOTHING.
  //
  // The fail-closed rule is right on the LIVE `lx_ask_user` path, where over-cap
  // copy means the tool refused and the model believes it asked. It INVERTS
  // here: this ask never went through the tool, the mission dialect has no such
  // cap, and the SPA MANUFACTURED the over-cap string itself by joining two
  // server fields — then refused its own construction. Nothing upstream refused
  // anything. Same shape as `topic`, which was moved to salvage on 2026-08-18.
  //
  // ⚠ SALVAGE BY DROPPING A WHOLE FIELD, NEVER BY TRUNCATING. This is legal
  // prose: cutting `recommended` mid-sentence can INVERT its meaning ("recomendamos
  // não incluir…" → "recomendamos…"), which is worse than showing less. So the
  // ladder is complete-field-or-nothing, matching `topic`'s drop-don't-mangle
  // idiom: both fields if they fit, else `why` alone if IT fits, else no
  // explanation at all — the question itself always survives.
  const explanation = pickExplanation(why, recommended);
  return {
    ...(typeof q.id === "string" ? { id: q.id } : {}),
    prompt,
    ...(explanation ? { explanation } : {}),
    ...(typeof q.topic === "string" ? { topic: q.topic } : {}),
  };
}

/**
 * Build the card for a parked mission, or `null` when there is nothing
 * honest to show.
 *
 * Returns null — rather than an empty card — when the briefing carries no
 * questions. An ask surface with no questions tells the lawyer something is
 * waiting on them and gives them no way to act, which is worse than the
 * assistant's own text still sitting in the thread.
 */
export function rehydrateAskFromMission(
  view: MissionAwaitingView,
  ctx: { missionId: string; sessionKey?: string },
): RehydratedAsk | null {
  const raw = questionsFromBriefing(view);
  if (!raw) return null;
  // ⚖ Normalise through `parseAskUserEvent` by handing it a wire-shaped
  // envelope, rather than re-deriving the questions here. Every PER-QUESTION
  // tolerance and refusal the live path applies — `topic` salvage,
  // `prompt`/`explanation` failing CLOSED, the choice caps — then applies
  // identically to a rehydrated ask. A second normaliser would drift from the
  // first, and the drift would show up as a card that renders after a reload
  // but not live, or worse the reverse.
  //
  // ⚠ The per-ASK question COUNT cap is the ONE refusal this plane does not
  // inherit — see the R1 note on the loop below. This sentence used to list it
  // among the shared refusals and was left stale by that change for one commit.
  const translated = raw.map(missionQuestionToAskShape).filter(Boolean);
  if (translated.length !== raw.length) return null; // partial translation is a broken dialect, not a card
  // ⚖ R1 — normalise ONE QUESTION AT A TIME, so every PER-QUESTION refusal and
  // tolerance the live path applies still applies here, while the per-ASK
  // question-COUNT cap does NOT.
  //
  // 🚨 Why the count cap must not fire on THIS plane, by the guide's own rule
  // (`lexide-ask-user-guide.md` §8-bis): posture follows RETRY COST. `lx_ask_user`
  // refuses because it sits inside a LIVE TURN where a retry is cheap and the model
  // is told. This plane reconstructs a park that ALREADY HAPPENED — no turn, no
  // model waiting, no retry — and the alternative to rendering is NOTHING, after
  // which the park is reaped as `awaiting_user_timeout` and reads as the lawyer
  // having declined. Measured live 2026-09-13: a 5-question park rendered
  // `radios=0, forms=0` on reload, and the whole card was discarded over the count.
  //
  // ⚠ This is the SECOND time this exact inversion was found in this file. The
  // EXPLANATION cap was moved to salvage on 2026-09-07 for the same reason (see
  // `missionQuestionToAskShape`); the COUNT dimension was left behind carrying the
  // justification that had just been retired beside it.
  //
  // ⚠ What deliberately does NOT change: a MALFORMED question still kills the whole
  // card, exactly as before. Salvaging a bad question was never ruled and would be a
  // different decision — this loop returns null on the first refusal.
  const questions: AskUserQuestion[] = [];
  for (const one of translated) {
    const parsedOne = parseAskUserEvent({
      data: { phase: "start", toolName: "lx_ask_user", toolInput: { questions: [one] } },
    });
    if (!parsedOne) return null;
    questions.push(...parsedOne.questions);
  }
  if (questions.length === 0) return null;
  return {
    card: {
      questions,
      ...(ctx.sessionKey ? { sessionKey: ctx.sessionKey } : {}),
      // ⚠ NOT a toolCallId — this ask never had one on this client. It is a
      // stable RENDER identity so a remount cannot inherit another ask's
      // answers, which is the whole job `askId` does (see its docblock). It is
      // deliberately derived from the mission so the same park rebuilds to the
      // same identity across reloads instead of a fresh one each time.
      askId: `mission:${ctx.missionId}`,
      ...(view.what ? { title: view.what } : {}),
    },
    answerable: ANSWERABLE_PARK_KINDS.has(view.kind),
    parkKind: view.kind,
    // A `user` park is the human gate by construction, so it is human-owed
    // without consulting a counterparty (it has none). Every other kind defers
    // to the shared F6 atom, which owns the "absent ⇒ concierge ⇒ human" default.
    humanOwed:
      view.kind === "user" ? true : isHumanOwedCounterparty(view.to),
    overAskCap: questions.length > ASK_USER_LIMITS.maxQuestions,
  };
}
